import PayInvoiceReason from "@auer-steel/Overrides/Widgets/PayInvoice/PayInvoiceReason";
import { PayInvoicePageContext } from "@auer-steel/Pages/PayInvoicePage";
import { PartialInvoicePaymentCollection } from "@auer-steel/Services/InvoicePaymentService";
import { AuerState } from "@auer-steel/Store/AuerState";
import submitInvoicePayments from "@auer-steel/Store/Pages/PayInvoice/Handlers/SubmitInvoicePayment";
import { validateSubmission } from "@auer-steel/Utilities/PayInvoiceUtilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import getLocalizedCurrency from "@insite/client-framework/Common/Utilities/getLocalizedCurrency";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonProps } from "@insite/mobius/Button";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import stubFalse from "lodash/stubFalse"
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: AuerState) => ({
    settings: getSettingsCollection(state),
    invoicePaymentCollection: state.payInvoice.invoicePaymentCollection,
    invoicePaymentValidationResult: state.payInvoice.invoiceValidationResult,
    language: state.context.session.language,
    currencySymbol: state.context.session.currency?.currencySymbol ?? "",
    invoicesPageUrl: getPageLinkByPageType(state, "InvoiceHistoryPage")?.url,
    invoiceDetailPageUrl: getPageLinkByPageType(state, "InvoiceDetailsPage")?.url,
});

const mapDispatchToProps = {
    submitInvoicePayments,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface PaymentOverViewAndSubmitStyles {
    container?: InjectableCss;
    amountsWrapper?: InjectableCss;
    amountWrapper?: InjectableCss;
    invoiceNumber?: TypographyProps;
    invoiceAmount?: TypographyProps;
    discountLabel?: TypographyProps;
    discountAmount?: TypographyProps;
    submitButton?: ButtonProps;
    spinnerWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    modal?: ModalProps;
    payInvoiceReasonModal?: ModalProps;
}

const styles: PaymentOverViewAndSubmitStyles = {
    container: {
        css: css`
            padding: 0 5px 5px;
        `,
    },
    amountsWrapper: {
        css: css`
            background: ${getColor("common.accent")};
            border: solid 1px ${getColor("common.border")};
            margin: 10px 0 10px;
        `,
    },
    amountWrapper: {
        css: css`
            display: flex;
            text-align: end;
            margin: 10px;
        `,
    },
    invoiceNumber: {
        css: css`
            flex: 1 0 70%;
        `,
    },
    invoiceAmount: {
        css: css`
            flex: 1 1 30%;
        `,
    },
    discountLabel: {
        css: css`
            flex: 1 0 70%;
            color: ${getColor("")}
        `,
    },
    discountAmount: {
        css: css`
            flex: 1 1 30%;
        `,
    },
    submitButton: {
        color: "secondary",
        css: css`
            width: 100%;
        `,
    },
    spinnerWrapper: {
        css: css`
            height: 50px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    modal: {
        cssOverrides: {
            modalBody: css`overflow-y: auto;`
        },
    },
    payInvoiceReasonModal: {
        cssOverrides: {
            modalTitle: css`padding: 15px`,
            modalContent: css`padding: 15px;`
        },
    },
};

let timerId: number | undefined = 0;

const PaymentOverviewAndSubmit: FC<Props> = props => {
    const fromDate = convertDateToDateOnlyString(new Date(Date.now() - props.settings.invoiceSettings.lookBackDays * 60 * 60 * 24 * 1000));
    const { invoicePayments, isSubmitting, overviewErrorMessage } = props.invoicePaymentCollection;
    const { invoicePaymentValidationResult } = props;
    if (!props.invoicePaymentCollection || !invoicePayments || invoicePayments.length < 1) {
        return null;
    }
    const [successModalIsOpen, setSuccessModalIsOpen] = React.useState(false);
    const [successModalMessage, setSuccessModalMessage] = React.useState("");

    const [partialInvoicePayments, setPartialInvoicePayments] = React.useState([] as PartialInvoicePaymentCollection[]);

    const [errorModalIsOpen, setErrorModalIsOpen] = React.useState(false);
    const [errorModalMessage, setErrorModalMessage] = React.useState("");
    const [errorAchAccountMessage, setErrorAchAccountMessage] = React.useState("");

    const invoiceTotal =  invoicePaymentValidationResult.invoiceProcessingInformation?.reduce<number>((sum, processingInfo) => {
        if (!processingInfo.isCredit) {
            return sum + processingInfo.actualPayment;
        }
        return sum;
    }, 0) ?? 0;

    const [payInvoiceReasonModalIsOpen, setPayInvoiceReasonModalIsOpen] = React.useState(false);

    const invoiceTotalDisplay = props.language
        ? getLocalizedCurrency({
            amount: invoiceTotal,
            language: props.language,
            currencySymbol: props.currencySymbol,
        })
        : invoiceTotal;

    const handleSubmit = (event: any) => {
        setErrorAchAccountMessage("");
        validateSubmission(props.invoicePaymentCollection).then((collectionWithErrors) => {
            if (collectionWithErrors) {
                setErrorAchAccountMessage(collectionWithErrors.overviewErrorMessage || "");
            }
            let reasonInvoiceModalIsOpen = false;
            props.invoicePaymentCollection.invoicePayments.forEach(invoicePayment => {
                if (invoicePayment.paymentAmount < invoicePayment.invoice.currentBalance) {
                    partialInvoicePayments.push({
                        InvoiceNumber: invoicePayment.invoice.id,
                        CurrentBalance: invoicePayment.invoice.currentBalanceDisplay,
                        PayAmount: props.language
                            ? getLocalizedCurrency({ amount: invoicePayment.paymentAmount, language: props.language, currencySymbol: props.currencySymbol })
                            : invoicePayment.paymentAmount.toString()
                    });

                    if (!reasonInvoiceModalIsOpen) {
                        reasonInvoiceModalIsOpen = invoicePayment.paymentAmount < invoicePayment.invoice.currentBalance;
                    }
                }
            });

            setPayInvoiceReasonModalIsOpen(reasonInvoiceModalIsOpen);
    
            if (!reasonInvoiceModalIsOpen) {
                submitInvoicePayments();
            }
        });
    };

    const submitInvoicePayments = (partialInvoicePayments?: PartialInvoicePaymentCollection[]) => {
        setPayInvoiceReasonModalIsOpen(false);
        props.submitInvoicePayments({
            invoicePaymentCollection: props.invoicePaymentCollection,
            partialInvoicePaymentCollection: partialInvoicePayments || [],
            onSuccess: message => {
                setSuccessModalIsOpen(true);
                setSuccessModalMessage(siteMessage("InvoiceHistory_invoicePaymentSubmitted") as string);
                timerId = setTimeout(handleSuccessModalDisplayTimeout, 10000);
            },
            onError: message => {
                setErrorModalIsOpen(true);
                setErrorModalMessage(siteMessage("InvoiceHistory_invoicePaymentFailed") as string);
                timerId = setTimeout(handleErrorModalDisplayTimeout, 10000);
            }
        });
    };

    const handleSuccessModalDisplayTimeout = () => {
        setSuccessModalIsOpen(false);
        if ((invoicePayments.length === 1)) {
            props.history.replace(`${props.invoiceDetailPageUrl}?invoiceNumber=${invoicePayments[0].invoice.invoiceNumber}&isForceRequestApi=true`);
        }
        else {
            props.history.replace(`${props.invoicesPageUrl}?fromDate=${fromDate}&isForceRequestApi=true`);
        }
    };

    const handleSuccessModalClose = () => {
        clearTimeout(timerId);
        setSuccessModalIsOpen(false);
        props.history.replace(`${props.invoicesPageUrl}?fromDate=${fromDate}&isForceRequestApi=true`);
    };

    const handleErrorModalDisplayTimeout = () => {
        setErrorModalIsOpen(false);
        (invoicePayments.length === 1) ?
            props.history.replace(`${props.invoiceDetailPageUrl}?invoiceNumber=${invoicePayments[0].invoice.invoiceNumber}`) :
            props.history.replace(`${props.invoicesPageUrl}?fromDate=${fromDate}` ?? "");
    };

    const handleErrorModalClose = () => {
        clearTimeout(timerId);
        setErrorModalIsOpen(false);
        (invoicePayments.length === 1) ?
            props.history.replace(`${props.invoiceDetailPageUrl}?invoiceNumber=${invoicePayments[0].invoice.invoiceNumber}`) :
            props.history.replace(`${props.invoicesPageUrl}?fromDate=${fromDate}` ?? "");
    };

    const handlePayInvoiceReasonModalClose = () => {
        setPayInvoiceReasonModalIsOpen(false);
        setPartialInvoicePayments([]);
    };

    return (
        <StyledWrapper {...styles.container}>
            <Typography weight="bold">{translate("Overview")}</Typography>
            <StyledWrapper {...styles.amountsWrapper}>
                {invoicePaymentValidationResult?.invoiceProcessingInformation?.map(validationResult => (
                    <>
                        <StyledWrapper key={validationResult.invoiceNumber} {...styles.amountWrapper}>
                            <Typography {...styles.invoiceNumber}>
                                {translate("Invoice # {0}:", validationResult.invoiceNumber)}
                            </Typography>
                            <Typography {...styles.invoiceAmount}>
                                {props.language
                                    ? getLocalizedCurrency({
                                        amount: validationResult.startingBalance - validationResult.endingBalance,
                                        language: props.language,
                                        currencySymbol: props.currencySymbol,
                                    })
                                    : validationResult.startingBalance - validationResult.endingBalance}
                            </Typography>
                        </StyledWrapper>
                        {validationResult.termsAmount > 0 &&
                            (<StyledWrapper {...styles.amountWrapper}>    
                                <Typography {...styles.invoiceNumber}>
                                    {translate("Terms Discount:")}
                                </Typography>
                                <Typography {...styles.invoiceAmount}>
                                    {`-${(props.language
                                        ? getLocalizedCurrency({
                                            amount: validationResult.termsAmount,
                                            language: props.language,
                                            currencySymbol: props.currencySymbol,
                                        })
                                        : validationResult.termsAmount)}`}
                                </Typography>
                            </StyledWrapper>)}
                    </>
                ))}
                <StyledWrapper {...styles.amountWrapper}>
                    <Typography weight="bold" {...styles.invoiceNumber}>
                        {translate("Total Amount to be Charged:")}
                    </Typography>
                    <Typography {...styles.invoiceAmount}>{invoiceTotalDisplay}</Typography>
                </StyledWrapper>
            </StyledWrapper>

            {isSubmitting ? (
                <StyledWrapper {...styles.spinnerWrapper}>
                    <LoadingSpinner {...styles.spinner}></LoadingSpinner>
                </StyledWrapper>
            ) : (
                <>
                    <Button {...styles.submitButton} onClick={handleSubmit}>
                        {translate("Submit Payment")}
                    </Button>
                    {(overviewErrorMessage || errorAchAccountMessage) && (
                        <Typography size="secondary" color="danger" weight={600}>
                            {overviewErrorMessage || errorAchAccountMessage}
                        </Typography>
                    )}
                </>
            )}

            <Modal
                {...styles.modal}
                sizeVariant="small"
                headline={translate("Invoice Payment Successful")}
                isOpen={successModalIsOpen}
                handleClose={handleSuccessModalClose}
            >
                {successModalMessage}
            </Modal>
            <Modal
                {...styles.modal}
                sizeVariant="small"
                headline={translate("Invoice Payment Error")}
                isOpen={errorModalIsOpen}
                handleClose={handleErrorModalClose}
            >
                {errorModalMessage}
            </Modal>
            <Modal {...styles.payInvoiceReasonModal} headline={translate("Reason")} isOpen={payInvoiceReasonModalIsOpen} handleClose={handlePayInvoiceReasonModalClose}>
                <PayInvoiceReason
                    partialInvoicePaymentCollection={partialInvoicePayments}
                    invoicePaymentCollection={props.invoicePaymentCollection}
                    onCancel={handlePayInvoiceReasonModalClose}
                    onSubmit={submitInvoicePayments}
                ></PayInvoiceReason>
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(PaymentOverviewAndSubmit)),
    definition: {
        group: "Pay Invoice" as any,
        allowedContexts: [PayInvoicePageContext],
    },
};

export default widgetModule;


